import { Router } from './routers/router.js';
import URI from '@dw/urijs-esm';
import buildURL, { viewIdToUrlPath } from './build-url';
import findLastIndex from 'lodash-es/findLastIndex';
import forEach  from 'lodash-es/forEach';
import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import merge from 'lodash-es/merge';
import * as history from '@dreamworld/web-util/history.js';
import * as auth from '../auth';
import * as analytics from '../../analytics/analytics.js';
import * as linkedIn from '../../analytics/linkedin.js';
import * as board from '../board';
import * as user from '../user';
import * as boardExplorerSelectors from '../board-explorer/selectors.js';
import * as selectors from './selectors.js';
import { serializeStackEntry } from './utils.js';
import { add } from 'lodash-es';

export const UPDATE_ROUTER = 'UPDATE_ROUTER';
export const ACCOUNT_ADDED = 'ACCOUNT_ADDED';
export const ACCOUNT_CHANGED = 'ACCOUNT_CHANGED';
export const ACCOUNT_REMOVED = 'ACCOUNT_REMOVED';
export const UPDATE_PRIVATE_BOARD = 'UPDATE_PRIVATE_BOARD';
/**
 * Instance of `Router` class
 */
let oRouter;

/**
 * Redux store.
 */
let _store;

/**
 * Initialize application routing
 */
export const init = (store) => {
  oRouter = new Router(store);
  _store = store;
}

/**
 * update private board.
 */
export const updatePrivateBoard = (value) => {
  return {
    type: UPDATE_PRIVATE_BOARD,
    value
  }
}

/**
 * Update browser url (using replace or push state based on parameter) and and trigger router flow if `dontInitRouting` is not true.
 * `navigate (url, replace)`
 *  - `url`: Its mandatory. Update browser url with new.
 *    - `url` is given as an router page object the it's convert to page url.
 *  - `replace`: It is a optional. Boolean value. Pass `true` to replace current url with new in browser history otherwise push into history.
 */
export const navigate = (url, replace, autoBack, autoBackCount) => {
  if(typeof url === 'object') {
    url = buildURL(url);
  }

  if(!url) {
    throw new Error("Invalid arguments passed.");
  }

  let uri = new URI();
  let newUri = new URI(url);

  if(!newUri.protocol()) {
    newUri.protocol(uri.protocol());
  }

  if(!newUri.host()) {
    newUri.host(uri.host());
  }

  if(newUri.toString() == uri.toString()) {
    console.warn('New navigate url is same as current url. So return without doing anything');
    return;
  }

  // When navigating from one card dialog to another card dialog, append the
  // current card context to the destination URL's `stack` query param so the
  // newly-opened card knows what to render behind it.
  if (_store) {
    const stackValue = _buildAutoStackValue(_store.getState(), newUri, replace, autoBack);
    if (stackValue) {
      newUri.setSearch('stack', stackValue);
      url = newUri.toString();
    }
  }

  if(replace) {
    oRouter.replaceState({}, '', url, autoBack, autoBackCount);
  } else {
    oRouter.pushState({}, '', url, autoBack, autoBackCount);
  }
}

/**
 * Builds the `stack` query value when navigating from one card dialog to
 * another card dialog. Returns '' to skip auto-stacking.
 * @param { Object } state Current redux state.
 * @param { Object } newUri URI instance for the destination URL.
 * @param { Boolean } replace `true` when this is a replaceState call.
 * @param { Boolean } autoBack `true` when navigation is part of a history-back flow.
 * @return { String } stack query value, or '' to skip.
 */
const _buildAutoStackValue = (state, newUri, replace, autoBack) => {
  if (replace || autoBack) {
    return '';
  }

  if (newUri.hasSearch('stack')) {
    return '';
  }

  if (selectors.dialogName(state) !== 'CARD') {
    return '';
  }

  const destSegments = newUri.segment();
  if (destSegments[1] !== 'board') {
    return '';
  }
  const destCardId = destSegments[3];
  if (!destCardId || !destCardId.startsWith('crd_')) {
    return '';
  }

  const currentCardId = selectors.cardId(state);
  if (!currentCardId || currentCardId === destCardId) {
    return '';
  }

  const currentAccountId = selectors.accountId(state);
  const currentBoardId = selectors.dialogBoardId(state);
  if (!currentAccountId || !currentBoardId) {
    return '';
  }
  const currentTab = selectors.cardTab(state) || 'description';

  // Carry the current card's AI chat state into its stack entry so the panel
  // stays open after navigating to another card.
  const currentChat = selectors.dialogAiChat(state);
  const existingEntries = selectors.dialogStack(state).map(serializeStackEntry);
  const currentEntry = serializeStackEntry({
    accountId: currentAccountId,
    boardId: currentBoardId,
    cardId: currentCardId,
    tab: currentTab,
    aichat: currentChat.isOpened ? (currentChat.chatId || 'new') : '',
    showingHistory: currentChat.isOpened && currentChat.showingHistory
  });
  return [...existingEntries, currentEntry].join(',');
}

/**
 * Given a destination URL, returns it carrying a `bg` (background board) param when it opens a card
 * on a board other than the one currently in view — but only from a page context, not over an open
 * card (there `_buildAutoStackValue`'s card-over-card stacker already keeps the origin board behind
 * the new card).
 *
 * Mirrors a global-search jump (`search-result-list-item`): a board-page panel (e.g. the AI Helper)
 * opening a cross-board card would otherwise flip the background to the destination's not-yet-loaded
 * board, blanking it. `bg` keeps the board the panel was opened from visible behind the card, and a
 * card close returns to it. A no-op for a same-board card, a non-card URL, or one already carrying
 * `bg`/`stack`.
 * @param { String } url destination URL/path
 * @return { String } url, possibly with `bg` appended
 * @public
 */
/** What of the current query a card opened from the page keeps: the open AI Helper, the board's zoom, the UI language. */
const PAGE_QUERY_KEPT = ['aichat', 'show-chat-history', 'zoom', 'lang'];

/**
 * The current URL at `path`, keeping only the page's lasting state (`PAGE_QUERY_KEPT`). Not a dialog, menu or sheet
 * open on it (`action`, `timer-action`, `column-action`...), nor the card it came from (`card-aichat`): they would
 * stay open over what it opens.
 * @param { String } path
 * @return { URI }
 * @public
 */
export const pageUri = (path) => {
  const uri = new URI().path(path);
  Object.keys(uri.query(true)).forEach((key) => PAGE_QUERY_KEPT.includes(key) || uri.removeSearch(key));
  return uri;
};

export const withBackgroundBoard = (url) => {
  if (!_store || !url) {
    return url;
  }
  const uri = new URI(url);
  const segments = uri.segment();
  if (segments[1] !== 'board' || !(segments[3] || '').startsWith('crd_')) {
    return url;
  }
  if (uri.hasSearch('bg') || uri.hasSearch('stack')) {
    return url;
  }

  const state = _store.getState();
  // Already on a card → the auto-stacker records the origin card and keeps its board behind.
  if (selectors.dialogName(state) === 'CARD') {
    return url;
  }

  const destBoardId = segments[2];
  const currentBoardId = selectors.pageBoardId(state);
  const currentAccountId = selectors.accountId(state);
  if (!currentBoardId || !currentAccountId || currentBoardId === destBoardId) {
    return url;
  }

  uri.setSearch('bg', `${currentAccountId}:${currentBoardId}`);
  return uri.toString();
}

/**
 * @param { String } cardId - Card id to test.
 * @return { Boolean } `true` when the previous browser-history entry's URL PATH
 *  is that card (matched on the path only, NOT the whole URL — a stacked card's
 *  URL carries the below card's id inside its `?stack=` param, which a substring
 *  match would false-positive on).
 */
const _isPreviousHistoryEntryCard = (cardId) => {
  if (!cardId) {
    return false;
  }
  const historyList = history.getHistoryList();
  const previousUrl = historyList && historyList[history.getCurrentPageIndex() - 1];
  if (!previousUrl) {
    return false;
  }
  return String(previousUrl).split(/[?#]/)[0].endsWith('/' + cardId);
}

/**
 * Closes the topmost card in the stack, revealing the card directly underneath
 * (or the page the card was opened from when nothing is stacked behind it).
 *
 * Prefer a plain `back()` — it preserves the below entry's context (e.g.
 * `referrer-page=VIEWS/SEARCH`, which stack entries don't store) and is what
 * makes same-board stacks close cleanly. But `back()` is unreliable for
 * cross-board stacks: a stray/duplicate history entry means one back doesn't
 * land on the card underneath, so the closing card + overlay linger and a second
 * close is needed. So only when history is out of sync
 * (`!_isPreviousHistoryEntryCard`) do we deterministically rebuild the below
 * card's URL so a single close reveals it.
 * @public
 */
export const closeTopCardFromStack = () => {
  const state = _store.getState();
  if (selectors.dialogName(state) !== 'CARD') {
    return;
  }

  const stack = selectors.dialogStack(state) || [];
  if (isEmpty(stack)) {
    // No card behind. If the card was opened over a retained background board
    // (a search jump, or an AI Helper chip, carrying `bg`), reveal that board.
    const bg = selectors.backgroundBoard(state);
    if (bg && bg.boardId !== selectors.dialogBoardId(state)) {
      const uri = new URI(`/${bg.accountId}/board/${bg.boardId}`);
      // The card may have been opened over the board while its AI Helper was open (a page-level
      // `?aichat` that rode along on the card URL). Carry that panel state back so closing the card
      // does not also close the Helper. A search jump has no Helper open, so this is a no-op there.
      const pageChat = selectors.pageAiChat(state);
      if (pageChat.isOpened) {
        uri.setSearch('aichat', pageChat.chatId || 'new');
        if (pageChat.showingHistory) {
          uri.setSearch('show-chat-history', 'true');
        }
      }
      navigate(uri.toString(), true);
      return;
    }
    // A card opened from a global view (`referrer-page=VIEWS`) returns to that
    // view. Navigate to it explicitly rather than a bare back(): opening a
    // cross-account card from the account-less `/views` page can leave history
    // such that back() lands on the board fallback (or nowhere), leaving the
    // card stuck in the URL. `views.currentId` still holds the origin view.
    if (selectors.referrerPage(state) === 'VIEWS') {
      const viewUrlPath = viewIdToUrlPath(get(state, 'views.currentId'));
      if (viewUrlPath) {
        navigate(`/views/${viewUrlPath}`, true);
        return;
      }
    }
    // Otherwise a plain back() returns to the real referrer (board, search, …).
    back();
    return;
  }

  const nextCard = stack[stack.length - 1];

  // The common case: the card underneath is exactly the previous history entry,
  // so back() reveals it and keeps its context. Only reconstruct when it isn't.
  if (_isPreviousHistoryEntryCard(nextCard.cardId)) {
    back();
    return;
  }

  const remainingStack = stack.slice(0, -1);
  // Build in the app's push order (stack → tab → card-aichat) so the URL matches
  // the below card's real history entry.
  const uri = new URI(`/${nextCard.accountId}/board/${nextCard.boardId}/${nextCard.cardId}`);
  if (!isEmpty(remainingStack)) {
    uri.setSearch('stack', remainingStack.map(serializeStackEntry).join(','));
  }
  if (nextCard.tab && nextCard.tab !== 'description') {
    uri.setSearch('tab', nextCard.tab);
  }
  if (nextCard.aichat) {
    uri.setSearch('card-aichat', nextCard.aichat);
    if (nextCard.showingHistory) {
      uri.setSearch('show-chat-history', 'true');
    }
  }

  // autoBack: rewind to the below card's real history entry (skipping the stray
  // that made a plain back() unreliable), cleaning it out of history; falls back
  // to replace when that exact entry isn't found.
  navigate(uri.toString(), true, true, history.getHistoryList().length);
}

/**
 * Refresh route data for the current page.
 * It's mainly used when page's context is changed. e.g. Card is moved to another board etc.
 * When such condition is detected by the UI app, it instructs router to refresh the data, so view can be updated
 * accordingly. e.g. View can read data from the updated path. Until router doesn't update the data, view can't know
 * where the card/page is moved.
 * @public
 */
export const refresh = () => {
  oRouter.refresh();
}

/**
 * Update query parameters of url and update browser url. If `push` is true then add new entry into history otherwise replace current entry
 * @param { Object } queryParams - Map of query parameters.
 *  - If value is `null | undefined` then remove query parameters from url otherwise add as new query parameters inot URL.
 * @param { Boolean } push - When it is `true`, pass new entry into history.
 */
export const setQueryParams = (queryParams, push) => {
  if(!queryParams) {
    return;
  }

  let uri = new URI();
  for(let param in queryParams) {
    if(queryParams[param] !== undefined && queryParams[param] !== null) {
      //Set new query parameter
      uri.setSearch(param, queryParams[param])
    }else {
      //Remove query parameter
      uri.removeSearch(param);
    }
  }

  navigate(uri.toString(), !push);
}

/**
 * Update hash query parameters of url and update browser url. If `push` is true then add new entry into history otherwise replace current entry
 * @param { Object } queryParams - Map of query parameters.
 *  - If value is `null | undefined` then remove query parameters from url otherwise add as new query parameters inot URL.
 * @param { Boolean } push - When it is `true`, pass new entry into history.
 */
export const setHashQueryParams = (queryParams, push) => {
  if(!queryParams) {
    return;
  }

  let uri = new URI();
  let uriHash = new URI(uri.fragment());
  for(let param in queryParams) {
    if(queryParams[param] !== undefined && queryParams[param] !== null) {
      //Set new query parameter
      uriHash.setSearch(param, queryParams[param])
    }else {
      //Remove query parameter
      uriHash.removeSearch(param);
    }
  }
  uri.fragment(uriHash.toString());
  navigate(uri.toString(), !push);
}

/**
 * Add action dialog query parameters into url.
 * When any action dialog is opened then replace new url into history.
 *  - e.g. open board context menu and after choose board setting option then open board setting dialog.
 * Otherwise push new url into history.
 * @param { String } action - Name of action dialog
 * @param { Object } actionParams - Action dialog additional query parameters
 */
export const setActionParam = (action, actionParams) => {
  if(!action) {
    return;
  }

  let addParams = { action };

  //Add additional query parameter in map
  if(actionParams) {
    for(let param in actionParams) {
      addParams[param] = actionParams[param];
    }
  }

  let uri = URI();
  if(!!uri.query(true).action) {
    setQueryParams(addParams);
    return;
  }

  setQueryParams(addParams, true);
}

/**
 * Remove action dialog query parameters from url and replace final URL with current url
 * @param { String } action - Name of action dialog
 * @param { Object } actionParams - Action dialog query paramerters which also removed from url
 */
export const removeActionParam = (action, actionParams) => {
  if(!action) {
    return;
  }

  //Return if url has no action query parameter with given value
  if(!new URI().hasSearch('action', action)) {
    return;
  }

  let removeParams = { action: null };

  //Add additional query parameter in remove params list
  if(actionParams) {
    for(let param in actionParams) {
      removeParams[param] = null;
    }
  }

  setQueryParams(removeParams);
}

/**
 * Router `back` method called.
 * @public
 */
export const back = () => {
  oRouter.back();
}

/**
 * Router `forward` method called.
 * @public
 */
export const forward = () => {
  oRouter.forward();
}

/**
 * Router `go` method called.
 * @param {Number} count you can move forward 2 pages by passing 2 and move backward 2 pages by passing -2.
 * @protected
 */
export const go = (count) => {
  oRouter.go(count);
}

/**
 * @param {Object} state state for this `url`.
 * @param {String} title title of opened page
 * @param {String} url New push url.
 * Router `pushState` method called.
 * @public
 */
export const pushState = (state = {}, title = '', url, autoBack, autoBackCount) => {
  oRouter.pushState(state, title, url, autoBack, autoBackCount);
}

/**
 * @param {Object} state state for this `url`.
 * @param {String} title title of opened page
 * @param {String} url New replace url.
 * Router `replaceState` method called.
 * @public
 */
export const replaceState = (state = {}, title = '', url, autoBack, autoBackCount) => {
  oRouter.replaceState(state, title, url, autoBack, autoBackCount);
}

/**
 *
 * @param { String } sUrl - Internal or Extenal URL
 * @return { Boolean } - `true` if passed url is an internal (application) url, otherwise return `false`
 */
export const isAppUrl = (sUrl) => {
  if(!sUrl) {
    return false;
  }

  const uri = new URI(sUrl);
  const urlHost = uri.protocol() + '://' + uri.host();
  const appUrl = window.location.protocol + '//' + window.location.host;
  const baseUrl = window.K && window.K.config && window.K.config.baseUrl;
  const oldBaseUrl = window.K && window.K.config && window.K.config.oldBaseUrl;
  if(urlHost === appUrl || urlHost === baseUrl || urlHost === oldBaseUrl) {
    return true;
  }
  
  const webAppBaseUrl = window.K && window.K.config && window.K.config.webAppBaseUrl;
  if(urlHost !== webAppBaseUrl) {
    return false;
  }
  
  const path = uri.pathname();
  const legacyUrls = selectors.legacyUrlsRegex();

  //Check url is internal link or not.
  let internalLink = false;
  forEach(legacyUrls, value => {
    const re = new RegExp(value);
    if (re.test(path)) {
      internalLink = true;
      return false;
    }
  });

  return internalLink;
}

/**
 * @param {String} sUrl
 * @returns {Boolean} given url path is same as a current url path.
 */
export const isSameUrlPath = (sUrl) => {
  if(!sUrl || sUrl.includes('/api')) {
    return false;
  }

  let uri = new URI(sUrl);
  let currentUri = new URI();
  return uri.pathname() === currentUri.pathname();
}

/**
 * If passed url is a PWA application then return it otherwise convert into PWA application url and return it.
 * @param { String } sUrl - Application URL
 * @return { String } - PWA application url
 */
export const getPWAUrl = (sUrl) => {
  if(!sUrl || !isAppUrl(sUrl)) {
    return sUrl;
  }

  const uri = new URI(sUrl);
  let path = uri.pathname();

  const webAppBaseUrl = window.K && window.K.config && window.K.config.webAppBaseUrl;
  uri.protocol(window.location.protocol);
  uri.host(window.location.host);

  if(webAppBaseUrl && sUrl.startsWith(webAppBaseUrl) && !path.startsWith('/app')) {
    uri.pathname(`/app/v1${path}`);
  }

  return uri.toString();
}

/**
 * Sets `user-actions` hash path.
 * @param {Boolean} replace Navigate with replaceState.
 */
export const openUserActionDialog = (replace = false) => {
  setHashPath('user-actions', replace);
}

/**
 * Sets `request-non-profit` hash path.
 */
export const openNonProfitRequestDialog = (replace = false) => {
  setHashPath('request-non-profit', replace);
}

/**
 * Sets `auth` hash path.
 * @param {Object} options auth dialog options.
 * @param {Boolean} replace Navigate with replaceState.
 */
export const openAuthDialog = ({mode = 'login', source, sourceParams = {}}, replace = false) => {
  let url = `auth?mode=${mode}`;
  if(source) {
    url = `${url}&source=${source}`;
    forEach(sourceParams, (value, key) => {
      url = `${url}&${key}=${value}`;
    });
  }
  let uri = new URI();
  uri.fragment(url);
  if(sourceParams && sourceParams.embedLoginSignup) {
    uri.addQuery('embed-login-signup', 'true');
  }
  navigate(uri.toString(), replace);
}

/**
 * Sets `leave-account` hash path with accountId.
 * @param {Boolean} replace Navigate with replaceState.
 */
export const openLeaveAccountDialog = (accountId, replace = false) => {
  setHashPath(`leave-account/${accountId}`, replace);
}

/**
 * Sets `account-switcher` hash path.
 * @param {Boolean} replace Navigate with replaceState.
 */
export const openAccountSwitcherDialog = (replace = false, forValue = '') => {
  if(forValue) {
    setHashPath(`account-switcher?for=${forValue}`, replace);
  } else {
    setHashPath('account-switcher', replace);
  }
}

/**
 * Sets `videos` hash path to open videos dialog.
 * @param {Boolean} replace Navigate with replaceState.
 */
export const openVideoTutorialDialog = ({name = '', trigger = '', url = ''}, replace = false) => {
  if(url) {
    setHashPath(trigger ? `videos?v-url=${encodeURIComponent(url)}&trigger=${trigger}`: `videos?v-url=${encodeURIComponent(url)}`, replace);
    return;
  }
  setHashPath(trigger ? `videos/${name}?trigger=${trigger}`: `videos/${name}`, replace);
}

/**
 * Sets `get-help` hash path.
 * @param {Boolean} replace Navigate with replaceState.
 */
export const openGetHelpDialog = (replace = false, mode) => {
  mode ? setHashPath(`get-help?mode=${mode}`, replace) : setHashPath('get-help', replace);
}

/**
 * Sets `keyboard-shortcuts` hash path.
 * @param {Boolean} replace Navigate with replaceState.
 */
export const openKeyboardShortcutsDialog = (replace = false) => {
  setHashPath('keyboard-shortcuts', replace);
}

/**
 * Sets `profile` hash path.
 * @param {Boolean} replace Navigate with replaceState.
 */
export const openProfileDialog = (replace = false) => {
  setHashPath('profile', replace);
}

/**
 * Sets `profile` hash path.
 * @param {Boolean} replace Navigate with replaceState.
 */
export const openLeaveKerikaDialog = (replace = false) => {
  setHashPath('leave-app', replace);
}

/**
 * Sets `calendar` hash path.
 * @param {String} service Name of the service. Possible values: 'google', 'outlook' or 'apple'
 * @param {Boolean} replace Navigate with replaceState.
 */
export const openCalendarDialog = (service, replace = false) => {
  setHashPath(`calendar?service=${service}`, replace);
}

/**
 * Sets `search` hash path to open search dialog.
 * @param {Boolean} replace Updates with replaceState or not.
 */
export const openSearchDialog = (replace = false) => {
  setHashPath(`search`, replace);
}

/**
 * Sets `invite-account-team` hash path to open invite account team dialog.
 * @param {Boolean} replace Updates with replaceState or not.
 */
 export const openInviteAccountTeamDialog = (replace = false) => {
  setHashPath(`invite-account-team`, replace);
}

/**
 * Sets `join-board` hash path to open join board dialog.
 * @param {Boolean} replace Updates with replaceState or not.
 */
export const openGenerateJoinBoardLinkDialog = (replace = false) => {
  setHashPath(`join-board`, replace);
}

/**
 * Sets `card-actions` hash path with provided query params.
 * @param {Object} params
 * @param {Boolean} replace Navigate with replaceState.
 */
export const openCardActinToolbar = ({cardId, accountId, boardId, columnId}, replace = false) => {
  setHashPath(`card-actions?cardId=${cardId}&accountId=${accountId}&boardId=${boardId}&columnId=${columnId}`, replace);
}

/**
 * Sets `date-picker` hash path.
 * @param {Boolean} replace Navigate with replaceState.
 */
export const openDatePicker = (replace = false) => {
  setHashPath('date-picker', replace);
}

/**
 * Opens `messages-dialog`.
 */
export const openMessagesDialog = (replace = false) => {
  setHashPath('messages', replace);
}

/**
 * Opens `broadcast-message-dialog`.
 */
export const openBroadcastMessagesDialog = (replace = false) => {
  setHashPath('broadcast-messages', replace);
}

/**
 * Open view as webpage for currently opened canvas page.
 */
export const openViewAsWebpageDialog = () => {
  setActionParam('view-as-webpage');
}

/**
 * Open translations dialog for currently opened board page.
 */
export const openTranslationsDialog = (replace = false) => {
  setHashPath('translations', replace);
}

/**
 * Open relative due-date dialog for currently opened board page.
 */
export const openRelativeDueDateDialog = (replace = false) => {
  setHashPath('relative-due-date', replace);
}

/**
 * Open use-case board settings dialog.
 * @param {Boolean} replace Navigate with replaceState.
 */
export const openUseCaseBoardSettingsDialog = (replace = false) => {
  setHashPath('use-case-board-settings', replace);
}

/**
 * Open change file storage dialog.
 * @param {String} storage new storage name.
 */
export const openChangeFileStorageDialog = (storage, replace = false) => {
  setQueryParams({'action': 'change-file-storage', 'storage': storage}, !replace);
}

/**
 * Open AI dialog.
 * @param {Object} data object
 *  - @param {Enum} context: 'CARD', 'BOARD', 'TEMPLATE', 'DASHBOARD'
 *  - @param {String} contextId: cardId, boardId, templateId, null
 *  - @param {String} contextField: 'title', 'description', 'comments', 'checklist', null
 *  - @param {String} contextFieldId: cardId, boardId, templateId, null
 * @param replace 
 */
export const openAiAssistantDialog = (data, replace = false) => {
  const queryString = buildQueryParams(data);
  if (queryString) {
    setQueryParams({ action: 'ai-assistant', ...data }, !replace);
    return;
  }
  setQueryParams({ action: 'ai-assistant' }, !replace);
}

/**
 * Open manage team confirm dialog.
 */
/**
 * Helper function to create query parameters string from data object
 * @param {Object} data Object containing parameters
 * @returns {String} Query parameters string
 */
const buildQueryParams = (data = {}) => {
  // Filter out null, undefined, and empty values from data
  return Object.entries(data)
    .filter(([_, value]) => {
      // Keep values that are truthy, or explicit zero/false values
      return value !== null && value !== undefined && value !== '';
    })
    .map(([key, value]) => {
      // Handle array values by joining with commas
      if (Array.isArray(value)) {
        return `${encodeURIComponent(key)}=${encodeURIComponent(value.join(','))}`;
      }
      
      // Handle normal values
      return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    })
    .join('&');
}

/**
 * Open manage team confirm dialog.
 */
export const openManageTeamConfirmDialog = (data = {}, replace = false) => {
  const queryString = buildQueryParams(data);
  const path = `manage-team-confirm${queryString ? `?${queryString}` : ''}`;
  setHashPath(path, replace);
}

/**
 * Open black list domain info dialog.
 * @param {Object} data Data to be passed to the dialog.
 *  - It can contain `emails`, `boardId`, `role` etc.
 * @param {Boolean} replace Navigate with replaceState.
 */
export const openBlackListDomainInfoDialog = (data = {}, replace = false) => {
  const queryString = buildQueryParams(data);
  const path = `blacklist-domain-info${queryString ? `?${queryString}` : ''}`;
  setHashPath(path, replace);
}

/**
 * Open customer balance dialog.
 */
export const openCustomerBalanceDialog = (replace = false) => {
  setHashPath('customer-balance', replace);
}

/**
 * Open change plan dialog.
 */
export const openChangePlanDialog = (data, replace = false) => {
  const queryString = buildQueryParams(data);
  setHashPath(`change-plan${queryString ? `?${queryString}` : ''}`, replace);
}

/**
 * Open switch to free plan dialog.
 */
export const openSwitchFreePlanDialog = (replace = false) => {
  setHashPath(`switch-to-free-plan`, replace);
}

/**
 * Open preview upcoming invoice dialog.
 */
export const openPreviewUpcomingInvoiceDialog = (replace = false) => {
  setHashPath(`preview-upcoming-invoice`, replace);
}

/**
 * Open the make-copy dialog.
 */
export const openMakeCopyDialog = () => {
  setActionParam('make-copy');
}

/**
 * Open the update billing address dialog.
 */
export const openUpdateBillingAddressDialog = (data = {}, replace = false) => {
  const queryString = buildQueryParams(data);
  const path = `update-billing-address${queryString ? `?${queryString}` : ''}`;
  setHashPath(path, replace);
}

/**
 * Open template learn-more dialog.
 * @param {Boolean} autoOpened learn more dialog is auto opened or not.
 */
export const openLearnMoreDialog = (autoOpened = false) => {
  if(autoOpened) {
    analytics.logTemplateEvent('template learn more auto presented');
    setQueryParams({'action': 'learn-more', 'auto-opened': 'true'}, true);
  } else {
    analytics.logTemplateEvent('template learn more opened');
    setActionParam('learn-more');
  }
}

/**
 * Open create-board-ai dialog.
 */
export const openCreateBoardAIDialog = (data, replace = false) => {
  const queryString = buildQueryParams(data);
  setHashPath(`create-board-ai${queryString ? `?${queryString}` : ''}`, replace);
}

/**
 * Open create-template-ai dialog.
 */
export const openCreateTemplateAIDialog = (data, replace = false) => {
  const queryString = buildQueryParams(data);
  setHashPath(`create-template-ai${queryString ? `?${queryString}` : ''}`, replace);
}

/**
 * Open create-board dialog when user is login otherwise open a login-and-signup dialog.
 * @param {Number} id template id.
 * @param {String} type board type.
 */
export const useTemplate = ({id, type = 'TASKBOARD', params = {}}, replace = false) => {
  const state = _store.getState();
  const userId = auth.selectors.currentUserId(state);
  id = id || selectors.pageBoardId(state);
  params = !isEmpty(params) ? params: {};
  params = merge({}, params, {'template': id});
  const mode = params && params.mode || 'signup';
  if(userId) {
    setActionParam('create-board', {'template': id, 'action-type': type === 'TASKBOARD' ? 'T': 'W'});
  } else {
    openAuthDialog({mode: mode, source: 'template', sourceParams: params}, replace);
  }
  analytics.logTemplateEvent('template used');
  linkedIn.logEvent(7099266);
}

/**
 * Sets hash path in URL.
 * @param {String} hashPath Hash path
 * @param {Boolean} replace Navigate with replaceState.
 */
export const setHashPath = (hashPath, replace = false) => {
  let uri = new URI();
  uri.fragment(hashPath);
  navigate(uri.toString(), replace);
}

/**
 * History Back to card given cardId.
 * @param {Number} cardId
 * @param {Number} boardId
 */
export const backToCard = ({accountId, cardId, boardId, tab, autoBackCount}) => {
  const historyList = history.getHistoryList();
  const urlWithoutTab = `/${accountId}/board/${boardId}/${cardId}`;
  const url = tab ? `${urlWithoutTab}?tab=${tab}`: urlWithoutTab;

  if(!historyList) {
    navigate(url);
    return;
  }

  if(historyList.length < 2) {
    navigate(url);
    return;
  }

  const backCount = history.getBackCount(url, autoBackCount);
  if(backCount) {
    navigate(url, false, true, autoBackCount);
    return;
  }

  const urlIndex = findLastIndex(historyList, (item) => {
    return item === urlWithoutTab || item.startsWith(urlWithoutTab);
  });

  let count = urlIndex >= 0 && urlIndex !== undefined ? (historyList.length - 1) - urlIndex: 0;
  count = autoBackCount && autoBackCount >= count ? count: 0;
  if(urlIndex < 0 || count < 1) {
    navigate(url);
    return;
  }
  oRouter.go(-count);
}

/**
 * History back to explore page of given account.
 * @returns
 */
export const backToHomeExplorePage = ({accountId, wideLayout, autoBackCount = 1, isUseCaseBoard = false}) => {
  const historyList = history.getHistoryList();
  const tab = getNewExplorerPageTab();
  const filter = getNewExplorerPageFilter(isUseCaseBoard);
  const url = wideLayout ? `/home/${filter}/${tab}` : `/${accountId}/home/${filter}/${tab}`;

  if(isUseCaseBoard) {
    navigate(url);
    navigate(getUseCaseBoardsDialogUrl(url));
    return;
  }

  if(!historyList) {
    navigate(url);
    return;
  }

  if(historyList.length < 2) {
    navigate(url);
    return;
  }

  const backCount = history.getBackCount(url, autoBackCount);
  if(backCount) {
    navigate(url, false, true, autoBackCount);
    return;
  }

  const urlIndex = findLastIndex(historyList, (item) => {
    if(wideLayout) {
      return item.startsWith('/home/');
    }
    return item.startsWith(`/${accountId}/home/`);
  });

  let count = urlIndex >= 0 && urlIndex !== undefined ? (historyList.length - 1) - urlIndex: 0;
  count = autoBackCount && autoBackCount >= count ? count: 0;
  if(urlIndex < 0 || count < 1) {
    navigate(url);
    return;
  }

  oRouter.go(-count);
}

const getUserJobRole = () => {
  const state = _store.getState();
  return user.selectors.jobRole(state);
}

const getUseCaseBoardsDialogUrl = (url) => {
  const jobRole = getUserJobRole();
  return jobRole ? `${url}?action=use-case-boards&industry=${jobRole}`: `${url}?action=use-case-boards`;
}

const getNewExplorerPageTab = () => {
  const state = _store.getState();
  const currentPageName = selectors.pageName(state);
  if(currentPageName === 'VIEWS') {
    return 'views';
  }

  if(currentPageName === 'BOARD') {
    const boardId = selectors.pageBoardId(state);
    const isTemplate = board.selectors.isTemplate(state, boardId);
    return isTemplate ? 'templates': 'boards';
  }

  return 'boards';
}

const getNewExplorerPageFilter = (isUseCaseBoard) => {
  const state = _store.getState();
  const currentPageName = selectors.pageName(state);
  if(currentPageName === 'VIEWS' || isUseCaseBoard) {
    return 'favorite';
  }

  if(currentPageName === 'BOARD') {
    const boardId = selectors.pageBoardId(state);
    const isFavorite = boardExplorerSelectors.favoriteBoard(state, boardId);
    return  isFavorite ? 'favorite': 'other';
  }

  return 'favorite';
}

// =============================================================================
// AI Chat Router Actions
// =============================================================================

/**
 * Opens page-level AI chat (e.g., board page context).
 * Sets `?aichat=new` or `?aichat=chatId` and removes `?show-chat-history`.
 *
 * @param {String} [chatId] - Optional chat ID. If not provided, opens new chat view.
 * @param {Boolean} [push=true] - Whether to push to history (true) or replace (false)
 */
export const openPageAiChat = (chatId = null, push = true) => {
  const value = chatId || 'new';
  setQueryParams({ aichat: value, 'show-chat-history': null }, push);
};

/**
 * Closes page-level AI chat.
 * Removes `?aichat` and `?show-chat-history` query params.
 *
 * @param {Boolean} [push=false] - Whether to push to history (true) or replace (false)
 */
export const closePageAiChat = (push = false) => {
  setQueryParams({ aichat: null, 'show-chat-history': null, messageId: null }, push);
};

/**
 * Opens dialog-level AI chat (e.g., card detail dialog context).
 * Sets `?card-aichat=new` or `?card-aichat=chatId` and removes `?show-chat-history`.
 *
 * @param {String} [chatId] - Optional chat ID. If not provided, opens new chat view.
 * @param {Boolean} [push=true] - Whether to push to history (true) or replace (false)
 */
export const openDialogAiChat = (chatId = null, push = true) => {
  const value = chatId || 'new';
  setQueryParams({ 'card-aichat': value, 'show-chat-history': null }, push);
};

/**
 * Closes dialog-level AI chat.
 * Removes `?card-aichat` and `?show-chat-history` query params.
 *
 * @param {Boolean} [push=false] - Whether to push to history (true) or replace (false)
 */
export const closeDialogAiChat = (push = false) => {
  setQueryParams({ 'card-aichat': null, 'show-chat-history': null, messageId: null }, push);
};

/**
 * Updates the chat ID for page-level AI chat.
 * Used when a new chat is created and we need to update the URL with the new chat ID.
 * Replaces `?aichat=new` with `?aichat=aic_xxx`.
 *
 * @param {String} chatId - The chat ID to set
 * @param {Boolean} [replace=true] - Whether to replace current history entry
 * @param {Boolean} [removeActionParams=false] - Whether to remove action query params from URL
 * @param {Array} [actionParams=[]] - Additional action query params to remove from URL
 */
export const setPageAiChatId = (chatId, replace = true, removeActionParams = false, actionParams = []) => {
  if (!chatId) return;
  const params = { aichat: chatId };
  if (removeActionParams) {
    params.action = null;
    actionParams.forEach(param => {
      params[param] = null;
    });
  }
  setQueryParams(params, !replace);
};

/**
 * Updates the chat ID for dialog-level AI chat.
 * Used when a new chat is created and we need to update the URL with the new chat ID.
 *
 * @param {String} chatId - The chat ID to set
 * @param {Boolean} [replace=true] - Whether to replace current history entry
 * @param {Boolean} [removeActionParams=false] - Whether to remove action query params from URL
 * @param {Array} [actionParams=[]] - Additional action query params to remove from URL
 */
export const setDialogAiChatId = (chatId, replace = true, removeActionParams = false, actionParams = []) => {
  if (!chatId) return;
  const params = { 'card-aichat': chatId };
  if (removeActionParams) {
    params.action = null;
    actionParams.forEach(param => {
      params[param] = null;
    });
  }
  setQueryParams(params, !replace);
};

/**
 * Shows page-level AI chat history view.
 * Adds `?show-chat-history=true` while preserving the current `?aichat` value.
 * If `?aichat` is not set, defaults to `new`.
 *
 * @param {Boolean} [push=true] - Whether to push to history (true) or replace (false)
 */
export const showPageAiChatHistory = (push = true) => {
  const uri = new URI();
  const currentAichat = uri.query(true).aichat || 'new';
  setQueryParams({ aichat: currentAichat, 'show-chat-history': 'true' }, push);
};

/**
 * Shows dialog-level AI chat history view.
 * Adds `?show-chat-history=true` while preserving the current `?card-aichat` value.
 * If `?card-aichat` is not set, defaults to `new`.
 *
 * @param {Boolean} [push=true] - Whether to push to history (true) or replace (false)
 */
export const showDialogAiChatHistory = (push = true) => {
  const uri = new URI();
  const currentCardAichat = uri.query(true)['card-aichat'] || 'new';
  setQueryParams({ 'card-aichat': currentCardAichat, 'show-chat-history': 'true' }, push);
};

/**
 * Hides page-level AI chat history view.
 * Removes `?show-chat-history` while preserving the current `?aichat` value.
 *
 * @param {Boolean} [push=false] - Whether to push to history (true) or replace (false)
 */
export const hidePageAiChatHistory = (push = false) => {
  setQueryParams({ 'show-chat-history': null }, push);
};

/**
 * Hides dialog-level AI chat history view.
 * Removes `?show-chat-history` while preserving the current `?card-aichat` value.
 *
 * @param {Boolean} [push=false] - Whether to push to history (true) or replace (false)
 */
export const hideDialogAiChatHistory = (push = false) => {
  setQueryParams({ 'show-chat-history': null }, push);
};